<template>
  <section :id="sectionId" class="section clean-neo problems-voices">
    <v-container class="py-16">
      <div class="problem-layout">
        <div class="problem-left reveal" data-reveal="curtain">
          <div class="problem-label">{{ t('problems.label') }}</div>
          <h2 class="problem-main-title gradient-text">
            {{ t('problems.titlePrefix') }} <span class="headline-accent">{{ t('problems.titleAccent') }}</span> {{ t('problems.titleSuffix') }}
          </h2>
          <p class="problem-main-copy">
            {{ t('problems.copy') }}
          </p>

          <ul class="problem-points">
            <li>
              <v-icon size="18">mdi-check-circle</v-icon>
              <span>{{ t('problems.points.one') }}</span>
            </li>
            <li>
              <v-icon size="18">mdi-check-circle</v-icon>
              <span>{{ t('problems.points.two') }}</span>
            </li>
            <li>
              <v-icon size="18">mdi-check-circle</v-icon>
              <span>{{ t('problems.points.three') }}</span>
            </li>
          </ul>

          <v-btn color="primary" size="large" class="problems-cta mt-8" @click="emit('nav', 'core')">
            {{ t('problems.cta') }}
          </v-btn>
        </div>

        <div class="problem-right">
          <article class="voice-card voice-card-w1 reveal" data-reveal="soft">
            <div class="voice-avatar" aria-hidden="true">
              <v-icon size="42">mdi-account-tie-woman-outline</v-icon>
            </div>
            <div class="voice-content">
              <div class="voice-meta"><strong>{{ t('problems.voices.julia.name') }}</strong> <span>{{ t('problems.voices.julia.role') }}</span></div>
              <p>{{ t('problems.voices.julia.question') }}</p>
            </div>
          </article>
          <article class="voice-card voice-card-w2 voice-card-shift-2 reveal" data-reveal="soft">
            <div class="voice-avatar" aria-hidden="true">
              <v-icon size="42">mdi-account-tie-outline</v-icon>
            </div>
            <div class="voice-content">
              <div class="voice-meta"><strong>{{ t('problems.voices.bruno.name') }}</strong> <span>{{ t('problems.voices.bruno.role') }}</span></div>
              <p>{{ t('problems.voices.bruno.question') }}</p>
            </div>
          </article>
          <article class="voice-card voice-card-w3 reveal" data-reveal="soft">
            <div class="voice-avatar" aria-hidden="true">
              <v-icon size="42">mdi-account-supervisor-outline</v-icon>
            </div>
            <div class="voice-content">
              <div class="voice-meta"><strong>{{ t('problems.voices.lola.name') }}</strong> <span>{{ t('problems.voices.lola.role') }}</span></div>
              <p>{{ t('problems.voices.lola.question') }}</p>
            </div>
          </article>
          <article class="voice-card voice-card-w4 voice-card-shift-1 reveal" data-reveal="soft">
            <div class="voice-avatar" aria-hidden="true">
              <v-icon size="42">mdi-account-check-outline</v-icon>
            </div>
            <div class="voice-content">
              <div class="voice-meta"><strong>{{ t('problems.voices.chris.name') }}</strong> <span>{{ t('problems.voices.chris.role') }}</span></div>
              <p>{{ t('problems.voices.chris.question') }}</p>
            </div>
          </article>
        </div>
      </div>
    </v-container>
  </section>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

type NavAnchor = 'core'

defineProps<{
  sectionId: string
}>()

const emit = defineEmits<{
  nav: [anchor: NavAnchor]
}>()

const { t } = useI18n()
</script>

<style scoped>
.section {
  scroll-margin-top: 80px;
  position: relative;
  overflow: hidden;
}

.clean-neo {
  background: #f8fafc;
}

.problems-voices {
  background: #edf4fa;
}

.problem-layout {
  display: grid;
  grid-template-columns: 1fr 1.25fr;
  gap: clamp(24px, 4.2vw, 56px);
  align-items: start;
}

.problem-main-title {
  font-size: clamp(28px, 2.6vw, 44px);
  font-weight: 800;
  line-height: 1.16;
  letter-spacing: 0;
  margin-top: 18px;
  max-width: 18ch;
}

.problem-label {
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: .24px;
  color: #475569;
  margin-bottom: 8px;
}

.problem-main-copy {
  color: #334155;
  font-size: clamp(18px, 1.4vw, 22px);
  line-height: 1.5;
  max-width: 44ch;
  margin-top: 30px;
}

.problem-points {
  margin-top: 30px;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 20px;
}

.problem-points li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 1.125rem;
  line-height: 1.45;
  margin: 0;
  color: #0f172a;
}

.problem-points .v-icon {
  margin-top: 4px;
  color: var(--lf-primary);
}

.problem-right {
  display: grid;
  gap: 30px;
  align-items: start;
}

.problems-cta {
  padding-inline: 26px;
  min-height: 54px;
  font-weight: 700;
}

.voice-card {
  display: flex;
  align-items: center;
  gap: clamp(16px, 1.5vw, 24px);
  padding: clamp(20px, 2vw, 30px) clamp(20px, 2.3vw, 36px);
  border-radius: 34px;
  background: rgba(255, 255, 255, .94);
  box-shadow: 0 14px 30px rgba(15, 23, 42, .06);
  width: 100%;
  max-width: var(--voice-card-width, 78%);
}

.voice-card-w1 {
  --voice-card-width: 76%;
}

.voice-card-w2 {
  --voice-card-width: 79%;
}

.voice-card-w3 {
  --voice-card-width: 74%;
}

.voice-card-w4 {
  --voice-card-width: 79%;
}

.voice-card-shift-1 {
  margin-left: clamp(34px, 3.6vw, 64px);
}

.voice-card-shift-2 {
  margin-left: clamp(44px, 4.8vw, 84px);
}

.voice-avatar {
  width: clamp(78px, 5.2vw, 112px);
  height: clamp(78px, 5.2vw, 112px);
  border-radius: 50%;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  background: radial-gradient(circle at 32% 28%, #f8fbff 0%, #e7eef5 58%, #d7e1ea 100%);
  border: 1px solid rgba(15, 23, 42, 0.06);
  color: #0b2d44;
}

.voice-content p {
  margin: 8px 0 0;
  font-size: clamp(12px, .78vw, 15px);
  line-height: 1.28;
  font-weight: 400;
  letter-spacing: 0;
  color: #0f172a;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.voice-meta {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 1.125rem;
}

.voice-meta strong {
  font-size: clamp(14px, .9vw, 18px);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -.1px;
}

.voice-meta span {
  font-size: clamp(11px, .68vw, 14px);
  color: #4f6474;
  font-weight: 600;
}

@media (max-width: 960px) {
  .problem-layout {
    grid-template-columns: 1fr;
  }

  .problem-right {
    order: 1;
  }

  .problem-left {
    order: 2;
  }

  .problem-main-title {
    font-size: clamp(26px, 7.2vw, 38px);
    max-width: 18ch;
  }

  .voice-meta strong {
    font-size: clamp(14px, 4.2vw, 17px);
  }

  .voice-meta span {
    font-size: clamp(11px, 3.2vw, 13px);
  }

  .voice-content p {
    font-size: clamp(12px, 3.6vw, 15px);
  }

  .voice-card-shift-1,
  .voice-card-shift-2 {
    margin-left: 0;
  }

  .voice-card {
    max-width: 100%;
  }
}
</style>
